import { useApi } from './api';

export interface DeepgramResponse {
  secureToken: string;
}

export interface TranscriptionResult {
  text?: string;
  error?: string;
  message_id?: number;
  s3_url?: string;
  presigned_url?: string;
  s3_key?: string;
  conversation_id?: string;
  created_new_conversation?: boolean;
}

export const useSpeechApi = () => {
  const { fetchWithAuth } = useApi();

  /**
   * Get a secure token for Deepgram API access
   * @returns A JWT token for Deepgram authentication
   */
  const getDeepgramToken = async (): Promise<string> => {
    try {
      const response = await fetchWithAuth('/api/deepgram-token');
      return response.secureToken;
    } catch (error) {
      console.error('Error fetching Deepgram token:', error);
      throw new Error('Failed to get speech recognition token');
    }
  };

  /**
   * Helper function to convert Float32Array to Int16Array for Deepgram
   * @param buffer Audio buffer in Float32 format
   * @returns Converted buffer in Int16 format
   */
  const convertFloat32ToInt16 = (buffer: Float32Array): Int16Array => {
    const l = buffer.length;
    const buf = new Int16Array(l);
    
    for (let i = 0; i < l; i++) {
      buf[i] = Math.min(1, buffer[i]) * 0x7FFF;
    }
    
    return buf;
  };

  /**
   * Determine supported MIME type for audio recording
   * @returns The best supported MIME type for the current browser
   */
  const getSupportedMimeType = (): string => {
    const types = [
      'audio/webm;codecs=opus',
      'audio/webm',
      'audio/mp4',
      'audio/ogg;codecs=opus',
      ''  // Empty string is a valid fallback that uses browser default
    ];
    
    for (const type of types) {
      if (type === '' || MediaRecorder.isTypeSupported(type)) {
        return type;
      }
    }
    
    return '';  // Default fallback
  };

  /**
   * Send audio file to backend for transcription and S3 storage
   * @param audioBlob The audio blob to transcribe
   * @param conversationId Optional conversation ID to associate with the recording
   * @param userId Optional user ID to associate with the recording
   * @returns Transcription result from the backend
   */
  const transcribeAudio = async (
    audioBlob: Blob, 
    conversationId?: string, 
    userId?: number
  ): Promise<TranscriptionResult> => {
    try {
      // Enhanced logging for debugging
      console.log("=== AUDIO TRANSCRIPTION REQUEST ===");
      console.log(`File type: ${audioBlob.type}`);
      console.log(`File size: ${audioBlob.size} bytes`);
      console.log(`Conversation ID: ${conversationId || 'Not provided'}`);
      console.log(`User ID: ${userId || 'Not provided'}`);
      
      if (!userId) {
        console.error("CRITICAL: Missing user_id parameter, transcription will fail");
        return { error: "Missing user_id parameter" };
      }
      
      // Create a FormData object to send the audio file
      const formData = new FormData();
      
      // Determine appropriate file extension based on MIME type
      let fileExtension = '.webm';
      if (audioBlob.type.includes('mp4')) {
        fileExtension = '.mp4';
      } else if (audioBlob.type.includes('ogg')) {
        fileExtension = '.ogg';
      } else if (audioBlob.type.includes('wav')) {
        fileExtension = '.wav';
      }
      
      console.log(`Using file extension: ${fileExtension}`);
      
      // The parameter name MUST be 'audio' to match what FastAPI expects
      formData.append('audio', audioBlob, `recording${fileExtension}`);
      
      // The user_id parameter is REQUIRED by the backend
      formData.append('user_id', String(userId));
      
      // Add conversation ID if available to store in S3
      if (conversationId) {
        // Skip temporary IDs (used as placeholders)
        if (conversationId.startsWith('temp_')) {
          console.warn(`Temporary conversation ID detected: ${conversationId}. S3 storage may not work.`);
        } else {
          console.log(`Adding conversation_id to form: ${conversationId}`);
          formData.append('conversation_id', conversationId);
        }
      } else {
        console.log("No conversation_id provided - A new conversation will be created");
      }
      
      // Log the FormData contents for debugging
      console.log("FormData contents for transcription request:");
      Array.from(formData.entries()).forEach(entry => {
        const [key, value] = entry;
        console.log(`- ${key}: ${key === 'audio' ? '[Audio Blob]' : value}`);
      });
      
      // Send the audio to the backend for transcription
      const data = await fetchWithAuth('/api/transcribe', {
        method: 'POST',
        // Don't set Content-Type header - let the browser set it with the boundary
        body: formData,
      });
      
      console.log("Transcription response received:", data);
      
      if (data.error) {
        console.error("Transcription error:", data.error);
        throw new Error(data.error);
      }
      
      if (data.s3_url) {
        console.log(`File stored in S3: ${data.s3_url}`);
      } else {
        console.warn("No S3 URL in response - file may not have been stored");
      }
      
      console.log("=== AUDIO TRANSCRIPTION COMPLETED ===");
      return data as TranscriptionResult;
    } catch (error) {
      console.error('Error transcribing audio:', error);
      return { error: error instanceof Error ? error.message : 'Unknown error during transcription' };
    }
  };

  /**
   * Refreshes a pre-signed URL for an S3 object
   * @param s3Key The S3 key (path in bucket) to generate a pre-signed URL for
   * @param expirationSeconds How long the URL should be valid for (in seconds)
   * @returns Promise with the new pre-signed URL or error
   */
  const refreshPresignedUrl = async (
    s3Key: string,
    expirationSeconds: number = 3600
  ): Promise<{ presigned_url: string } | { error: string }> => {
    try {
      console.log(`Refreshing pre-signed URL for S3 key: ${s3Key}`);
      
      const formData = new FormData();
      formData.append('s3_key', s3Key);
      formData.append('expiration_seconds', String(expirationSeconds));
      
      const response = await fetchWithAuth('/api/generate-presigned-url', {
        method: 'POST',
        body: formData
      });
      
      console.log('Pre-signed URL response:', response);
      
      if (response.success && response.presigned_url) {
        console.log(`Successfully refreshed pre-signed URL for ${s3Key}`);
        return {
          presigned_url: response.presigned_url
        };
      } else {
        console.error(`Failed to get presigned URL: ${response.error || 'Unknown error'}`);
        throw new Error(response.error || 'Failed to generate pre-signed URL');
      }
    } catch (error) {
      console.error('Error refreshing pre-signed URL:', error);
      return {
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  };

  return {
    getDeepgramToken,
    convertFloat32ToInt16,
    getSupportedMimeType,
    transcribeAudio,
    refreshPresignedUrl
  };
}; 